import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import { useAuthorization } from "../hooks/useAuthorization";
import type { AuthorizationRequirement } from "../types/permissions";
import { AccessDenied } from "./AccessDenied";

export interface ProtectedRouteProps extends AuthorizationRequirement {
  /** Optional child elements to render when authorized. If omitted, renders <Outlet />. */
  children?: ReactNode;
  /** Destination path for unauthenticated users (defaults to "/login"). */
  loginPath?: string;
  /** Custom fallback component to render when authenticated but unauthorized. */
  fallback?: ReactNode;
}

/**
 * Route protection guard supporting authentication enforcement, role clearance,
 * and permission-based access control.
 *
 * Examples:
 * ```tsx
 * // Enforce login only:
 * <ProtectedRoute />
 *
 * // Enforce specific permission:
 * <ProtectedRoute requiredPermission="students.read">
 *   <ClassroomRoster />
 * </ProtectedRoute>
 *
 * // Enforce role:
 * <ProtectedRoute requiredRole="Teacher" />
 * ```
 */
export function ProtectedRoute({
  children,
  loginPath = "/login",
  fallback,
  requiredPermission,
  requiredPermissions,
  anyPermission,
  requiredRole,
  allowedRoles,
}: ProtectedRouteProps) {
  const { isAuthenticated, isInitialized } = useAuth();
  const { check } = useAuthorization();
  const location = useLocation();

  // 1. Guard against initial hydration flicker
  if (!isInitialized) {
    return null;
  }

  // 2. Redirect unauthenticated users to login, preserving destination in location.state
  if (!isAuthenticated) {
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // 3. Evaluate permission and role requirements
  const requirement: AuthorizationRequirement = {
    requiredPermission,
    requiredPermissions,
    anyPermission,
    requiredRole,
    allowedRoles,
  };

  const isAuthorized = check(requirement);

  // 4. Handle unauthorized access for authenticated users
  if (!isAuthorized) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return <AccessDenied />;
  }

  // 5. Render children or nested router outlet
  return children ? <>{children}</> : <Outlet />;
}
